/*
  LPBC — behavior of the lpbc-* landing page sections (template: page.lp-baggage-claim).
  Custom elements, so they also (re)initialize on their own when a section is re-rendered in the theme editor.

  The picked bag (from the belt or the finder) is painted on every [data-lpbc-*] element of the page:
    [data-lpbc-pick-link]   href of the picked product
    [data-lpbc-pick-name]   "<Color> <Model>"
    [data-lpbc-pick-price]  price of the picked product
*/
(() => {
  if (window.LPBC) return;

  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const mod = (value, size) => ((value % size) + size) % size;
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const easeOut = (p) => 1 - Math.pow(1 - p, 3);
  const easeInOut = (p) => (p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2);
  const escape = (text) => String(text).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);
  // Early access (snippets/early-access.liquid): visitors with the code (html.early-access) see the price with the code
  // taken off and the regular price as "was", like on product pages. Checked on every paint, so it follows the flag.
  const offer = (price, was, eaPrice) =>
    eaPrice && document.documentElement.classList.contains('early-access') ? { price: eaPrice, was: price } : { price, was };

  const LPBC = (window.LPBC = {
    pick: null,

    setPick(pick) {
      this.pick = pick;
      this.paint();
    },

    paint() {
      if (!this.pick) return;
      document.querySelectorAll('[data-lpbc-pick-link]').forEach((link) => (link.href = this.pick.url));
      document.querySelectorAll('[data-lpbc-pick-name]').forEach((element) => (element.textContent = this.pick.name));
      document.querySelectorAll('[data-lpbc-pick-price]').forEach((element) => (element.textContent = this.pick.price));
    }
  });

  /* ---------- Split-flap board ---------- */
  const CELLS = 28;
  const PHONE_CELLS = 20;
  const GLYPHS = 'ABCDEFGHIJKLMNOPRSTUVWXYZ0123456789';

  // A phone shows 20 columns and lets the middle line wrap onto a fourth row.
  function format(lines, phone) {
    if (!phone) return [lines[0], lines[1], '', lines[2]];
    const words = String(lines[1] || '').split(' ');
    let first = '';
    while (words.length && (first + ' ' + words[0]).trim().length <= PHONE_CELLS) first = (first + ' ' + words.shift()).trim();
    return [lines[0], first, words.join(' '), lines[2]];
  }

  function createBoard(rows) {
    const cells = [];
    let timer = null;

    rows.forEach((row) => {
      row.innerHTML = '';
      const list = [];
      for (let i = 0; i < CELLS; i++) {
        const cell = document.createElement('span');
        cell.className = 'bc__cell';
        cell.innerHTML = '<i class="bc__half bc__half--top"></i><i class="bc__half bc__half--bottom"></i><i class="bc__flap bc__flap--top"></i><i class="bc__flap bc__flap--bottom"></i>';
        row.appendChild(cell);
        list.push({ top: cell.children[0], bottom: cell.children[1], flapTop: cell.children[2], flapBottom: cell.children[3], value: ' ', queue: [] });
      }
      cells.push(list);
    });

    const show = (cell, value) => {
      cell.value = value;
      cell.top.textContent = cell.bottom.textContent = value;
    };

    function flip(cell) {
      const next = cell.queue.shift();
      if (next === cell.value) return;
      const previous = cell.value;
      cell.value = next;
      cell.top.textContent = next;
      cell.flapTop.textContent = previous;
      cell.flapBottom.textContent = next;
      cell.flapTop.style.visibility = cell.flapBottom.style.visibility = 'visible';
      cell.flapTop.animate([{ transform: 'scaleY(1)' }, { transform: 'scaleY(0)' }], { duration: 28, fill: 'forwards' });
      cell.flapBottom.animate([{ transform: 'scaleY(0)' }, { transform: 'scaleY(1)' }], { duration: 28, delay: 28, fill: 'both' }).onfinish = () => {
        cell.bottom.textContent = next;
        cell.flapTop.style.visibility = cell.flapBottom.style.visibility = 'hidden';
      };
    }

    function tick() {
      let busy = false;
      cells.forEach((list) => list.forEach((cell) => {
        if (cell.queue.length) {
          flip(cell);
          busy = busy || cell.queue.length > 0;
        }
      }));
      timer = busy ? setTimeout(tick, 64) : null;
    }

    return {
      set(lines, instant, phone) {
        format(lines, phone).forEach((line, rowIndex) => {
          const text = String(line || '').toUpperCase().slice(0, phone ? PHONE_CELLS : CELLS);
          cells[rowIndex].forEach((cell, column) => {
            const target = text.charAt(column) || ' ';
            if (instant || reduced()) {
              cell.queue = [];
              show(cell, target);
              return;
            }
            if (target === cell.value && !cell.queue.length) return;
            cell.queue = [];
            for (let i = 0, steps = 1 + ((column + rowIndex * 3) % 5); i < steps; i++) cell.queue.push(GLYPHS.charAt(Math.floor(Math.random() * GLYPHS.length)));
            cell.queue.push(target);
          });
        });
        if (!reduced() && !instant && !timer) tick();
      },
      stop() {
        clearTimeout(timer);
        timer = null;
      }
    };
  }

  /* ---------- Baggage claim belt (from the Baggage Claim section; "Claim" links to the product) ---------- */
  const SLATS = 72;
  const COAST = 900; // ms, how long a thrown belt keeps its speed
  const SETTLE_SPEED = 0.0016; // slots per ms below which a thrown belt picks its stop
  const MAX_SPEED = 0.03; // slots per ms, so a hard throw stays readable
  const BOARD_EVERY = 170; // ms between board changes while the belt is moving
  const SHEEN = ['#2e3137', '#272a2f'];

  customElements.define(
    'lpbc-belt',
    class extends HTMLElement {
      connectedCallback() {
        const root = this.querySelector('.bc');
        const stage = root.querySelector('[data-stage]');
        const bags = [...root.querySelectorAll('[data-bag]')];
        if (!stage || !bags.length) return;
        const priceOf = (data) => offer(data.price, data.was, data.eaPrice);
        bags.forEach((bag) => bag.setAttribute('aria-label', `${bag.dataset.name}, ${priceOf(bag.dataset).price}`));

        const canvas = root.querySelector('[data-belt]');
        const ctx = canvas.getContext('2d');
        const pillar = root.querySelector('[data-pillar]');
        const grab = root.querySelector('[data-grab]');
        const status = root.querySelector('[data-status]');
        const rate = root.querySelector('[data-belt-rate]');
        const board = createBoard([...root.querySelectorAll('[data-row]')]);
        const phone = window.matchMedia('(max-width: 759px)');
        const count = bags.length;
        const cruise = Number(root.dataset.speed) / 10000; // slots per ms

        let geo = null;
        let wasWide = !phone.matches;
        let mode = 'rest'; // rest, cruise, drag, coast, tween
        let pos = 0; // belt position in bag slots, bag i sits at the pickup point when pos = -i
        let velocity = 0;
        let drawnVelocity = 0;
        let tween = null;
        let drag = null;
        let frame = null;
        let last = 0;
        let active = -1;
        let boardStamp = 0;
        let boardDirty = false;
        let startTimer = null;
        let visible = true;
        const state = bags.map((el, index) => ({ el, aspect: Number(el.dataset.aspect) || 0.7, sway: index % 2 ? 1 : -0.8, rock: 0, raise: 0, width: 0, height: 0, x: 0, z: 0, depth: -1 }));

        const point = (u, r) => {
          let t;
          u = mod(u, 1);
          if (u < geo.a) return { x: -geo.L + 2 * geo.L * (u / geo.a), z: r };
          u -= geo.a;
          if (u < geo.b) {
            t = Math.PI * (u / geo.b);
            return { x: geo.L + r * Math.sin(t), z: r * Math.cos(t) };
          }
          u -= geo.b;
          if (u < geo.a) return { x: geo.L - 2 * geo.L * (u / geo.a), z: -r };
          u -= geo.a;
          t = Math.PI * (u / geo.b);
          return { x: -geo.L - r * Math.sin(t), z: -r * Math.cos(t) };
        };
        const project = (x, y, z) => {
          const p = geo.f / (geo.f - z);
          return { x: geo.cx + x * p, y: geo.cy + (z * geo.k - y) * p, p };
        };

        const layout = () => {
          const rect = stage.getBoundingClientRect();
          const w = rect.width;
          const h = rect.height;
          if (!w || !h) return;
          const wide = !phone.matches;
          const dpr = Math.min(window.devicePixelRatio || 1, 2);
          const L = wide ? 0.3 * w : 0.13 * w;
          const R = wide ? 0.158 * w : 0.31 * w;
          const band = wide ? 0.076 * w : 0.125 * w;
          const mid = R - band / 2;
          const perimeter = 4 * L + 2 * Math.PI * mid;
          geo = {
            w, h, L, R, mid,
            inner: R - band,
            a: (2 * L) / perimeter,
            b: (Math.PI * mid) / perimeter,
            f: wide ? 1.05 * w : 1.3 * w,
            k: wide ? 0.56 : 0.52,
            cx: w / 2,
            cy: h * 0.6,
            skirt: wide ? 0.018 * w : 0.03 * w,
            island: wide ? 0.009 * w : 0.016 * w,
            unit: wide ? Math.min(0.158 * w, h * 0.4) : Math.min(0.25 * w, h * 0.36)
          };
          geo.pick = geo.a / 2;
          geo.slot = (perimeter / count) * (geo.f / (geo.f - mid));
          canvas.width = Math.round(w * dpr);
          canvas.height = Math.round(h * dpr);
          ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
          state.forEach((bag) => {
            bag.height = bag.aspect >= 1 ? (geo.unit * 1.16) / bag.aspect : geo.unit;
            bag.width = bag.height * bag.aspect;
            bag.el.style.width = bag.width + 'px';
            bag.el.style.height = bag.height + 'px';
          });
          const base = project(0, geo.island, 0);
          pillar.style.transform = `translate3d(${base.x - pillar.offsetWidth / 2}px,${base.y - pillar.offsetHeight}px,0)`;
          pillar.style.zIndex = 500;
          if (active >= 0 && wide !== wasWide) board.set(lines(active), true, !wide);
          wasWide = wide;
          draw();
        };

        const edge = (from, to, radius, height, reverse) => {
          for (let i = 0, steps = 70; i <= steps; i++) {
            const at = point(from + ((to - from) * (reverse ? steps - i : i)) / steps, radius);
            const screen = project(at.x, height, at.z);
            if (i === 0 && !reverse) ctx.moveTo(screen.x, screen.y);
            else ctx.lineTo(screen.x, screen.y);
          }
        };
        const ring = (radius, height) => {
          ctx.beginPath();
          for (let i = 0, steps = 180; i <= steps; i++) {
            const at = point(i / steps, radius);
            const screen = project(at.x, height, at.z);
            if (i === 0) ctx.moveTo(screen.x, screen.y);
            else ctx.lineTo(screen.x, screen.y);
          }
          ctx.closePath();
        };

        const drawBelt = () => {
          const nearFrom = -geo.b / 2;
          const nearTo = geo.a + geo.b / 2;
          ctx.clearRect(0, 0, geo.w, geo.h);

          ctx.save();
          ctx.fillStyle = 'rgba(0, 0, 0, .42)';
          ctx.filter = 'blur(14px)';
          ring(geo.R * 1.03, -geo.skirt * 1.5);
          ctx.fill();
          ctx.restore();

          const skirt = ctx.createLinearGradient(0, geo.cy, 0, geo.cy + geo.R);
          skirt.addColorStop(0, '#8f949c');
          skirt.addColorStop(1, '#4d5057');
          ctx.fillStyle = skirt;
          ctx.beginPath();
          edge(nearFrom, nearTo, geo.R, 0, false);
          edge(nearFrom, nearTo, geo.R, -geo.skirt, true);
          ctx.closePath();
          ctx.fill();

          ring(geo.R, 0);
          ctx.fillStyle = '#1a1c20';
          ctx.fill();

          const offset = mod(pos / count, 1);
          for (let i = 0; i < SLATS; i++) {
            const u0 = i / SLATS + offset;
            const u1 = (i + 1) / SLATS + offset;
            const a = point(u0, geo.inner), b = point(u0, geo.R), c = point(u1, geo.R), d = point(u1, geo.inner);
            const pa = project(a.x, 0, a.z), pb = project(b.x, 0, b.z), pc = project(c.x, 0, c.z), pd = project(d.x, 0, d.z);
            ctx.fillStyle = SHEEN[i % 2];
            ctx.beginPath();
            ctx.moveTo(pa.x, pa.y);
            ctx.lineTo(pb.x, pb.y);
            ctx.lineTo(pc.x, pc.y);
            ctx.lineTo(pd.x, pd.y);
            ctx.closePath();
            ctx.fill();
            ctx.strokeStyle = 'rgba(0, 0, 0, .55)';
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(pa.x, pa.y);
            ctx.lineTo(pb.x, pb.y);
            ctx.stroke();
          }

          const pool = project(0, 0, geo.mid);
          const light = ctx.createRadialGradient(pool.x, pool.y, 0, pool.x, pool.y, geo.unit * 1.1);
          light.addColorStop(0, 'rgba(255, 255, 255, .17)');
          light.addColorStop(1, 'rgba(255, 255, 255, 0)');
          ctx.fillStyle = light;
          ring(geo.R, 0);
          ctx.fill();

          state.forEach((bag) => {
            const base = project(bag.x, 0, bag.z);
            ctx.fillStyle = 'rgba(0, 0, 0, .5)';
            ctx.beginPath();
            ctx.ellipse(base.x, base.y, (bag.width * base.p) / 2, Math.max(4, ((bag.width * base.p) / 2) * geo.k * 0.34), 0, 0, Math.PI * 2);
            ctx.fill();
          });

          ctx.fillStyle = '#3a3d44';
          ctx.beginPath();
          edge(nearFrom, nearTo, geo.inner, 0, false);
          edge(nearFrom, nearTo, geo.inner, geo.island, true);
          ctx.closePath();
          ctx.fill();

          const top = ctx.createLinearGradient(0, geo.cy - geo.inner, 0, geo.cy + geo.inner);
          top.addColorStop(0, '#111316');
          top.addColorStop(1, '#212328');
          ctx.fillStyle = top;
          ring(geo.inner, geo.island);
          ctx.fill();
          ctx.strokeStyle = 'rgba(255, 255, 255, .28)';
          ctx.lineWidth = 1;
          ctx.stroke();

          ctx.strokeStyle = 'rgba(255, 255, 255, .34)';
          ring(geo.R, 0);
          ctx.stroke();

          const mark = project(0, -geo.skirt * 0.5, geo.R);
          const size = geo.skirt * 0.32;
          ctx.fillStyle = getComputedStyle(root).getPropertyValue('--bc-accent');
          ctx.beginPath();
          ctx.moveTo(mark.x, mark.y - size);
          ctx.lineTo(mark.x + size * 1.5, mark.y + size);
          ctx.lineTo(mark.x - size * 1.5, mark.y + size);
          ctx.closePath();
          ctx.fill();
        };

        const draw = () => {
          if (!geo) return;
          const accel = velocity - drawnVelocity;
          drawnVelocity = velocity;
          state.forEach((bag, index) => {
            const at = point(geo.pick + (index + pos) / count, geo.mid);
            bag.x = at.x;
            bag.z = at.z;
            const screen = project(at.x, 0, at.z);
            const lean = clamp(accel * 34000 + velocity * 900, -7, 7) * bag.sway;
            bag.rock += (lean - bag.rock) * 0.14;
            bag.raise += ((index === active && mode === 'rest' ? 1 : 0) - bag.raise) * 0.16;
            const scale = screen.p * (1 + bag.raise * 0.06);
            const y = screen.y - bag.raise * geo.unit * 0.06;
            bag.el.style.transform = `translate3d(${screen.x.toFixed(2)}px,${y.toFixed(2)}px,0) translate(-50%,-100%) rotate(${bag.rock.toFixed(2)}deg) scale(${scale.toFixed(4)})`;
            bag.el.style.setProperty('--bc-swing', (bag.rock * 2.4).toFixed(2) + 'deg');
            const depth = Math.round(500 + (at.z / geo.R) * 400);
            if (depth !== bag.depth) {
              bag.depth = depth;
              bag.el.style.zIndex = depth;
            }
          });
          drawBelt();
        };

        const lines = (index) => {
          const data = bags[index].dataset;
          const { price, was } = priceOf(data);
          return [data.name, data.fits, price + (was ? '   WAS ' + was : '')];
        };

        const syncBoard = (now, force) => {
          if (active < 0) return;
          if (!force && now - boardStamp < BOARD_EVERY) {
            boardDirty = true;
            return;
          }
          boardStamp = now;
          boardDirty = false;
          board.set(lines(active), false, phone.matches);
        };

        const syncBar = () => {
          const data = bags[active].dataset;
          grab.href = data.url;
          const { price } = priceOf(data);
          grab.textContent = `${root.dataset.grabText} · ${price}`;
          rate.innerHTML = data.rating ? `<span class="lpbc-stars" aria-hidden="true">★</span> ${escape(data.rating)} from ${escape(data.count)} reviews of the ${escape(data.name)}` : '';
          LPBC.setPick({ name: data.name, price, url: data.url });
        };

        const setActive = (index, now) => {
          if (index === active) return;
          if (active >= 0) bags[active].classList.remove('is-active');
          active = index;
          bags[active].classList.add('is-active');
          syncBar();
          syncBoard(now, false);
        };

        const currentIndex = () => mod(Math.round(-pos), count);
        const settled = () => state.every((bag) => Math.abs(bag.rock) < 0.02 && Math.abs(bag.raise - (bags[active] === bag.el ? 1 : 0)) < 0.004);

        const arrive = (now) => {
          pos = Math.round(pos);
          velocity = 0;
          tween = null;
          mode = 'rest';
          root.classList.remove('is-moving');
          setActive(currentIndex(), now);
          syncBoard(now, true);
          status.textContent = bags[active].getAttribute('aria-label') + '. ' + bags[active].dataset.fits;
        };

        const loop = (now) => {
          frame = null;
          const dt = Math.min(40, now - (last || now));
          last = now;
          if (mode === 'cruise') {
            velocity += (cruise - velocity) * (1 - Math.exp(-dt / 320));
            pos += velocity * dt;
          } else if (mode === 'coast') {
            velocity *= Math.exp(-dt / COAST);
            pos += velocity * dt;
            if (Math.abs(velocity) < SETTLE_SPEED) settle(velocity >= 0 ? 1 : -1, now);
          } else if (mode === 'tween') {
            const progress = tween.duration ? clamp((now - tween.start) / tween.duration, 0, 1) : 1;
            const previous = pos;
            pos = tween.from + (tween.to - tween.from) * tween.ease(progress);
            velocity = dt ? (pos - previous) / dt : 0;
            if (progress >= 1) arrive(now);
          }
          if (mode !== 'rest') setActive(currentIndex(), now);
          if (boardDirty && now - boardStamp >= BOARD_EVERY) syncBoard(now, true);
          draw();
          if (visible && (mode !== 'rest' || !settled())) kick();
        };

        const kick = () => {
          if (frame === null) frame = requestAnimationFrame(loop);
        };

        const begin = (next) => {
          mode = next;
          root.classList.add('is-moving');
          last = 0;
          kick();
        };

        // One stop in the direction of travel, entered with a one way ease, so the bag never rocks back.
        const settle = (direction, now) => {
          const to = direction > 0 ? Math.ceil(pos + 0.03) : Math.floor(pos - 0.03);
          const speed = Math.max(Math.abs(velocity), 0.0007);
          tween = { from: pos, to, start: now, duration: reduced() ? 0 : clamp((3 * Math.abs(to - pos)) / speed, 240, 1500), ease: easeOut };
          begin('tween');
        };

        const goTo = (index, slow) => {
          clearTimeout(startTimer);
          const delta = mod(-index - pos + count / 2, count) - count / 2;
          const duration = slow ? Math.min(2600, 700 + 210 * Math.abs(delta)) : Math.min(1500, 340 + 210 * Math.abs(delta));
          tween = { from: pos, to: pos + delta, start: performance.now(), duration: reduced() ? 0 : duration, ease: easeInOut };
          begin('tween');
        };

        const bagAt = (x, y) => {
          let found = -1;
          let top = -1;
          state.forEach((bag, index) => {
            const box = bag.el.getBoundingClientRect();
            if (x >= box.left && x <= box.right && y >= box.top && y <= box.bottom && bag.depth > top) {
              top = bag.depth;
              found = index;
            }
          });
          return found;
        };

        const onDown = (event) => {
          if (event.button) return;
          clearTimeout(startTimer);
          root.classList.add('is-dragging', 'is-used');
          drag = { id: event.pointerId, x: event.clientX, from: pos, stamp: event.timeStamp, lastX: event.clientX, moved: 0 };
          stage.setPointerCapture(event.pointerId);
          velocity = 0;
          mode = 'drag';
          root.classList.add('is-moving');
          kick();
        };

        const onMove = (event) => {
          if (!drag || event.pointerId !== drag.id) return;
          const elapsed = Math.max(1, event.timeStamp - drag.stamp);
          const step = (event.clientX - drag.lastX) / geo.slot;
          drag.moved = Math.max(drag.moved, Math.abs(event.clientX - drag.x));
          drag.lastX = event.clientX;
          drag.stamp = event.timeStamp;
          pos = drag.from + (event.clientX - drag.x) / geo.slot;
          velocity += (clamp(step / elapsed, -MAX_SPEED, MAX_SPEED) - velocity) * 0.42;
          kick();
        };

        const onUp = (event) => {
          if (!drag || event.pointerId !== drag.id) return;
          const now = performance.now();
          const idle = event.timeStamp - drag.stamp > 90;
          const moved = drag.moved;
          const hit = moved < 7 ? bagAt(event.clientX, event.clientY) : -1;
          drag = null;
          root.classList.remove('is-dragging');
          if (idle) velocity = 0;
          if (moved < 7) {
            velocity = 0;
            if (hit >= 0) goTo(hit);
            else settle(1, now);
            return;
          }
          if (Math.abs(velocity) > SETTLE_SPEED && !reduced()) {
            begin('coast');
            return;
          }
          if (Math.abs(velocity) > 0.0002) {
            settle(velocity > 0 ? 1 : -1, now);
            return;
          }
          tween = { from: pos, to: Math.round(pos), start: now, duration: reduced() ? 0 : 240, ease: easeOut };
          begin('tween');
        };

        const onEnter = (event) => {
          if (event.pointerType !== 'mouse' || reduced() || !cruise || mode !== 'rest') return;
          root.classList.add('is-moving', 'is-used');
          startTimer = setTimeout(() => {
            if (mode === 'rest') begin('cruise');
          }, 450);
        };

        const onLeave = (event) => {
          if (event.pointerType !== 'mouse' || drag) return;
          clearTimeout(startTimer);
          if (mode === 'cruise') settle(1, performance.now());
          else if (mode === 'rest') root.classList.remove('is-moving');
        };

        const onKey = (event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          goTo(active + (event.key === 'ArrowRight' ? -1 : 1));
        };

        const onPrev = () => {
          root.classList.add('is-used');
          goTo(active + 1);
        };
        const onNext = () => {
          root.classList.add('is-used');
          goTo(active - 1);
        };

        // Cutouts can be swapped in the editor, so each bag takes its proportions from the loaded image.
        state.forEach((bag) => {
          const image = bag.el.querySelector('img');
          const measure = () => {
            if (!image.naturalWidth) return;
            bag.aspect = image.naturalWidth / image.naturalHeight;
            layout();
          };
          if (image.complete) measure();
          else image.addEventListener('load', measure, { once: true });
        });

        stage.addEventListener('pointerdown', onDown);
        stage.addEventListener('pointermove', onMove);
        stage.addEventListener('pointerup', onUp);
        stage.addEventListener('pointercancel', onUp);
        stage.addEventListener('pointerenter', onEnter);
        stage.addEventListener('pointerleave', onLeave);
        stage.addEventListener('keydown', onKey);
        root.querySelector('[data-prev]').addEventListener('click', onPrev);
        root.querySelector('[data-next]').addEventListener('click', onNext);

        this.resize = new ResizeObserver(layout);
        this.watcher = new IntersectionObserver((entries) => {
          visible = entries[entries.length - 1].isIntersecting;
          if (visible) {
            last = 0;
            kick();
          }
        });
        layout();
        this.resize.observe(stage);
        this.watcher.observe(stage);

        active = 0;
        bags[0].classList.add('is-active');
        syncBar();
        board.set(lines(0), true, phone.matches);
        kick();

        // One orchestrated moment: on load the belt brings the first bag in, so a phone visitor sees that it moves.
        if (!reduced() && count > 1) {
          pos = Math.min(3, count - 1);
          draw();
          this.intro = setTimeout(() => {
            if (mode === 'rest' && !root.classList.contains('is-used')) goTo(0, true);
          }, 500);
        }

        this.destroy = () => {
          cancelAnimationFrame(frame);
          clearTimeout(startTimer);
          clearTimeout(this.intro);
          board.stop();
          this.resize.disconnect();
          this.watcher.disconnect();
        };
      }

      disconnectedCallback() {
        if (this.destroy) this.destroy();
      }
    }
  );

  /* ---------- Finder: four questions, one in-stock bag ---------- */
  customElements.define(
    'lpbc-finder',
    class extends HTMLElement {
      connectedCallback() {
        const source = this.querySelector('[data-lpbc-json]');
        if (!source) return;
        this.data = JSON.parse(source.textContent);
        this.step = 0;
        this.answers = {};
        this.$ = (selector) => this.querySelector(selector);

        this.$('[data-fx-opts]').addEventListener('click', (event) => {
          const option = event.target.closest('[data-value]');
          if (!option) return;
          this.answers[this.data.questions[this.step].key] = option.dataset.value;
          if (++this.step < this.data.questions.length) this.ask();
          else this.result();
        });
        this.$('[data-fx-back]').addEventListener('click', () => {
          if (this.step > 0) {
            this.step--;
            this.ask();
          }
        });
        this.$('[data-fx-again]').addEventListener('click', () => {
          this.step = 0;
          this.answers = {};
          this.$('[data-fx-res]').hidden = true;
          this.$('[data-fx-q]').hidden = false;
          this.ask();
          this.$('[data-fx-q]').scrollIntoView({ block: 'center' });
        });
        this.$('[data-fx-colors]').addEventListener('click', (event) => {
          const chip = event.target.closest('[data-index]');
          if (chip) this.show(Number(chip.dataset.index));
        });
        this.ask();
      }

      ask() {
        const questions = this.data.questions;
        const question = questions[this.step];
        this.$('[data-fx-step]').textContent = `Question ${this.step + 1} of ${questions.length}`;
        this.$('[data-fx-bar]').style.width = (100 * this.step) / questions.length + '%';
        this.$('[data-fx-back]').hidden = this.step === 0;
        this.$('[data-fx-title]').textContent = question.text;
        this.$('[data-fx-opts]').innerHTML = question.answers
          .map(([value, label]) => `<button type="button" class="lpbc-fx__opt" data-value="${escape(value)}">${escape(label)}</button>`)
          .join('');
      }

      // Every answer changes the pick. The same map is in the LP brief (part 8).
      match() {
        const a = this.answers;
        const models = this.data.models;
        const has = (key) => models[key] && models[key].colors.length;
        const notes = this.data.notes;
        let key;
        let note = '';
        if (a.size === '17') {
          key = 'xl';
          if (a.carry === 'hand') note = notes.xl_hand;
        } else if (a.carry === 'hand') {
          key = 'duffle';
          if (a.pairs === '5') note = notes.duffle_five;
        } else if (a.pairs === '2' && a.days === 'short') {
          key = 'commuter';
        } else {
          key = a.size === '15' ? 'xl' : 'signature';
        }
        // A model with nothing in stock falls back to the Signature, then to anything that is in stock.
        if (!has(key)) {
          key = has('signature') ? 'signature' : Object.keys(models).find(has);
          note = '';
        }
        return { key, note };
      }

      result() {
        const { key, note } = this.match();
        if (!key) return;
        const model = this.data.models[key];
        const a = this.answers;
        this.model = model;
        const why = [
          a.pairs === '2' ? `Holds ${model.pairs.toLowerCase()}, so your 1 or 2 have room.` : `Holds ${model.pairs.toLowerCase()}. You bring ${a.pairs === '3' ? '3' : '4 or 5'}.`,
          `Fits shoes up to size ${model.size}.`,
          `${model.days}${a.days === 'long' ? ', enough for a week away' : ''}.`,
          `${model.carry}. ${model.laptop}.`
        ];
        this.$('[data-fx-why]').innerHTML = why.map((line) => `<li>${escape(line)}</li>`).join('');
        const noteEl = this.$('[data-fx-note]');
        noteEl.hidden = !note;
        noteEl.textContent = note;
        this.$('[data-fx-colors]').innerHTML = model.colors
          .map((color, index) => `<button type="button" class="lpbc-chip" data-index="${index}" style="--c: ${escape(color.swatch)};" aria-pressed="false"><i></i>${escape(color.name)}</button>`)
          .join('');
        this.show(0);
        this.$('[data-fx-q]').hidden = true;
        this.$('[data-fx-res]').hidden = false;
        this.$('[data-fx-bar]').style.width = '100%';
        this.$('[data-fx-res]').scrollIntoView({ block: 'start', behavior: reduced() ? 'auto' : 'smooth' });
      }

      show(index) {
        const model = this.model;
        const color = model.colors[index];
        const name = `${color.name} ${model.name}`;
        const image = this.$('[data-fx-img]');
        if (color.image) image.src = color.image;
        image.alt = name;
        this.$('[data-fx-name]').textContent = name;
        const { price, was } = offer(color.price, color.was, color.ea_price);
        this.$('[data-fx-price]').innerHTML = escape(price) + (was ? `<s>${escape(was)}</s>` : '');
        this.$('[data-fx-rate]').innerHTML = color.rating ? `<span class="lpbc-stars" aria-hidden="true">★</span> ${escape(color.rating)} from ${escape(color.count)} reviews` : '';
        const cta = this.$('[data-fx-cta]');
        cta.href = color.url;
        cta.textContent = `${this.data.button_text} · ${price}`;
        this.querySelectorAll('[data-fx-colors] [data-index]').forEach((chip, key) => chip.setAttribute('aria-pressed', key === index));
        LPBC.setPick({ name, price, url: color.url });
      }
    }
  );

  /* ---------- Sticky CTA: shows after the hero and steps aside for the last call ---------- */
  customElements.define(
    'lpbc-sticky',
    class extends HTMLElement {
      connectedCallback() {
        this.pastHero = false;
        this.atEnd = false;
        const sync = () => this.firstElementChild.classList.toggle('is-on', this.pastHero && !this.atEnd);
        this.observers = [
          ['.lpbc-hero', (entry) => (this.pastHero = !entry.isIntersecting)],
          ['.lpbc-last', (entry) => (this.atEnd = entry.isIntersecting)]
        ].flatMap(([selector, update]) => {
          const target = document.querySelector(selector);
          if (!target) return [];
          const observer = new IntersectionObserver(([entry]) => {
            update(entry);
            sync();
          });
          observer.observe(target);
          return [observer];
        });
        LPBC.paint();
      }

      disconnectedCallback() {
        this.observers.forEach((observer) => observer.disconnect());
      }
    }
  );

  document.addEventListener('shopify:section:load', () => LPBC.paint());
})();
